import { useNavigationGuardContext } from "@jugl-web/domain-resources/common/providers/NavigationGuardProvider";
import { LoadingSpinner, MultiSectionLayout } from "@jugl-web/ui-kit";
import { Button } from "@jugl-web/ui-kit/primitives/Button";
import { useTranslations } from "@jugl-web/utils";
import { useFormSubmittingState } from "@jugl-web/utils/form/hooks/useFormSubmittingState";
import React, { useCallback, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useVoiceAccount } from "../../../../hooks/useVoiceAccount";
import { useUpdateVoiceSettings } from "../../hooks/useUpdateVoiceSettings";
import { VoiceTransferFormValues } from "../../types";
import { getVoiceTransferFormValues } from "../../utils/getVoiceFormValues";
import { TransferCallSection } from "../TransferCallSection";
import { WhenToUseSection } from "../WhenToUseSection";
import { ReactComponent as ArrowRightIcon } from "./assets/arrow-right.svg";

export const TransferCallsTab: React.FC = () => {
  const { t } = useTranslations();
  const { account } = useVoiceAccount();
  const { updateVoiceSettings } = useUpdateVoiceSettings();
  const { requestNavigation, registerDirtySource } =
    useNavigationGuardContext();
  const { isSubmitting, setIsSubmitting } = useFormSubmittingState();

  const form = useForm<VoiceTransferFormValues>({
    defaultValues: getVoiceTransferFormValues(account),
    reValidateMode: "onBlur",
  });

  const { handleSubmit, reset, formState } = form;

  const resetForm = useCallback(() => {
    reset(getVoiceTransferFormValues(account));
  }, [account, reset]);

  const handleSaveChanges = useCallback(
    async (formData: VoiceTransferFormValues) => {
      try {
        setIsSubmitting(true);

        const isUpdated = await updateVoiceSettings({
          metadata: {
            handover_phone: formData.transfer_enabled
              ? formData.transfer_number
              : null,
            when_to_handover: formData.when_to_handover.trim() || null,
          },
        });

        if (isUpdated) {
          reset(formData);
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [reset, setIsSubmitting, updateVoiceSettings]
  );

  useEffect(() => {
    return registerDirtySource("twilio-voice-transfer-tab", formState.isDirty);
  }, [formState.isDirty, registerDirtySource]);

  return (
    <FormProvider {...form}>
      <MultiSectionLayout
        className="h-full"
        classNames={{
          content: "overflow-y-auto",
        }}
        footer={
          formState.isDirty ? (
            <div className="flex gap-4 border-0 border-t border-solid border-dark-100 bg-white px-8 py-4 max-lg:px-4">
              <Button
                variant="tertiary"
                size="md"
                className="w-30"
                onClick={() => requestNavigation(resetForm)}
                isDisabled={isSubmitting}
              >
                {t({
                  id: "common.cancel",
                  defaultMessage: "Cancel",
                })}
              </Button>
              <Button
                variant="primary"
                size="md"
                className="w-75"
                onClick={handleSubmit(handleSaveChanges)}
                isDisabled={isSubmitting}
                iconEnd={
                  isSubmitting ? (
                    <LoadingSpinner size="xs" />
                  ) : (
                    <ArrowRightIcon />
                  )
                }
              >
                {t({
                  id: "common.save",
                  defaultMessage: "Save",
                })}
              </Button>
            </div>
          ) : undefined
        }
      >
        <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-8 p-8 max-lg:gap-4 max-lg:p-4">
          <TransferCallSection />
          <WhenToUseSection />
        </div>
      </MultiSectionLayout>
    </FormProvider>
  );
};
